import React from 'react';
import { Box, Typography, Stack, Tooltip } from '@mui/material';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../../theme';
import { isoFromDMY } from '../../utils/format';

// One palette, one type scale and one radius for both punch screens, so the
// employee panel and the approver table read as the same product.
export const INK = '#0F172A';
export const BODY = '#475569';
export const MUTED = '#98A0AE';
export const LINE = '#EEF0F6';
export const LINE_STRONG = '#E6EAF1';
export const HOVER = '#FBFAFF';
export const RADIUS = '7px';

export const AMBER = '#B45309';
export const GREEN = '#16A34A';
export const ROSE = '#E11D48';

export const MISSING = {
    checkin: { label: 'Check-in missing', dot: AMBER },
    checkout: { label: 'Check-out missing', dot: '#0369A1' },
    both: { label: 'Both punches missing', dot: ROSE },
};
export const missingOf = (k) => MISSING[k] || { label: 'Punch correction', dot: MUTED };

export const STATUS = {
    pending: { label: 'Pending', color: AMBER, bg: '#FEF6EC', border: '#F6E0C2' },
    approved: { label: 'Approved', color: '#15803D', bg: '#F0FDF4', border: '#C4EBD1' },
    rejected: { label: 'Rejected', color: '#BE123C', bg: '#FEF3F5', border: '#F8CDD7' },
};
export const statusOf = (k) => STATUS[k] || { label: k || '—', color: '#64748B', bg: '#F5F7FA', border: LINE_STRONG };

export const needsIn = (t) => t === 'both' || t === 'checkin';
export const needsOut = (t) => t === 'both' || t === 'checkout';

export const pickList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.requests)) return data.requests;
    if (Array.isArray(data?.punchRequests)) return data.punchRequests;
    if (Array.isArray(data?.missedPunches)) return data.missedPunches;
    return [];
};

// Split rather than one long string — the weekday sits under the date as a
// quiet second line, which keeps the date column narrow and even.
export const dayParts = (d) => {
    const iso = isoFromDMY(d);
    const t = iso ? new Date(iso) : null;
    if (!t || Number.isNaN(t.getTime())) return { date: d ? String(d) : '—', weekday: '' };
    return {
        date: t.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        weekday: t.toLocaleDateString('en-GB', { weekday: 'long' }),
    };
};

export function Label({ children, sx }) {
    return (
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#6E6B99', textTransform: 'uppercase', letterSpacing: 0.6, ...sx }}>
            {children}
        </Typography>
    );
}

export function StatusPill({ status }) {
    const s = statusOf(status);
    return (
        <Stack
            direction="row"
            spacing={0.7}
            sx={{
                alignItems: 'center', height: 24, px: 1.1, borderRadius: '999px',
                bgcolor: s.bg, border: `1px solid ${s.border}`, flexShrink: 0,
            }}
        >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.color }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: s.color, whiteSpace: 'nowrap' }}>{s.label}</Typography>
        </Stack>
    );
}

export function MissingTag({ type }) {
    const m = missingOf(type);
    return (
        <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', mt: 0.35 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: m.dot, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11.5, color: MUTED, whiteSpace: 'nowrap' }}>{m.label}</Typography>
        </Stack>
    );
}

function TimeCell({ icon: Icon, value, tone }) {
    const has = Boolean(value);
    return (
        <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center', minWidth: 74 }}>
            <Icon sx={{ fontSize: 15, color: has ? tone : '#CBD2DD' }} />
            <Typography
                sx={{
                    fontSize: 13, fontWeight: has ? 700 : 400, color: has ? '#334155' : '#B6BDC9',
                    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                }}
            >
                {value || '—'}
            </Typography>
        </Stack>
    );
}

// Times always occupy the same two slots, so every row lines up down the page
// whether one punch is present or neither is.
export function PunchTimes({ inTime, outTime }) {
    return (
        <Stack direction="row" spacing={1.6} sx={{ alignItems: 'center' }}>
            <TimeCell icon={LoginRoundedIcon} value={inTime} tone={GREEN} />
            <TimeCell icon={LogoutRoundedIcon} value={outTime} tone={ROSE} />
        </Stack>
    );
}

export function Reason({ text, lines = 2, sx }) {
    if (!text) return <Typography sx={{ fontSize: 13, color: '#B6BDC9', ...sx }}>No reason given</Typography>;
    return (
        <Tooltip arrow title={text} enterDelay={600}>
            <Typography
                sx={{
                    fontSize: 13, color: BODY, lineHeight: 1.5,
                    display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    ...sx,
                }}
            >
                {text}
            </Typography>
        </Tooltip>
    );
}

// iOS-style segmented control: one bordered track, active segment lifted on
// white. Reads as a deliberate control instead of four loose chips.
export function Segmented({ items, value, onChange }) {
    return (
        <Stack
            direction="row"
            sx={{
                p: '3px', gap: '3px', bgcolor: '#F4F3FA', border: `1px solid ${LINE_STRONG}`,
                borderRadius: '9px', flexShrink: 0,
            }}
        >
            {items.map((it) => {
                const on = it.key === value;
                return (
                    <Stack
                        key={it.key}
                        direction="row"
                        spacing={0.7}
                        onClick={() => onChange(it.key)}
                        role="tab"
                        aria-selected={on}
                        sx={{
                            alignItems: 'center', height: 30, px: 1.5, borderRadius: RADIUS, cursor: 'pointer',
                            bgcolor: on ? '#fff' : 'transparent',
                            boxShadow: on ? '0 1px 3px rgba(16,24,40,0.10)' : 'none',
                            transition: 'background-color .15s, box-shadow .15s',
                            '&:hover': { bgcolor: on ? '#fff' : 'rgba(255,255,255,0.6)' },
                        }}
                    >
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: on ? INK : '#6B7280', whiteSpace: 'nowrap' }}>
                            {it.label}
                        </Typography>
                        {it.count > 0 && (
                            <Box
                                sx={{
                                    minWidth: 18, height: 18, px: 0.5, borderRadius: '999px',
                                    bgcolor: on ? PRIMARY_LIGHT : '#E8E9EF',
                                    border: `1px solid ${on ? PRIMARY_BORDER : 'transparent'}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: on ? PRIMARY : '#6B7280' }}>{it.count}</Typography>
                            </Box>
                        )}
                    </Stack>
                );
            })}
        </Stack>
    );
}

export const approveBtn = {
    bgcolor: '#F0FDF4', color: '#15803D', border: '1px solid #C4EBD1',
    fontWeight: 700, textTransform: 'none', borderRadius: RADIUS, boxShadow: 'none',
    '&:hover': { bgcolor: '#DCFCE7', borderColor: '#9FDDB4' },
    '&.Mui-disabled': { bgcolor: '#F8FAFC', color: '#B6BDC9', borderColor: LINE },
};

export const rejectBtn = {
    bgcolor: '#fff', color: '#BE123C', border: '1px solid #F3D3DA',
    fontWeight: 700, textTransform: 'none', borderRadius: RADIUS, boxShadow: 'none',
    '&:hover': { bgcolor: '#FEF3F5', borderColor: '#EEB9C4' },
    '&.Mui-disabled': { bgcolor: '#F8FAFC', color: '#B6BDC9', borderColor: LINE },
};
